// Pick the weakest / strongest minion in scope and kill it.
// The Black Plague / Great Famine pattern: at end of turn, the
// extremum of the affected minions dies. If the scope is empty and
// `dispel_if_none` is set, the source aura dispels itself instead.
//
// Pure picker + host-dispatched kill. The tie case (codex "choose one")
// is handled by the server emit path (automationChoice.emitKillByPower),
// which stages a player pick over the tied extremum; this pure resolver
// stays as the single-source picker + the no-tie fast path.
//
// Host adapter contract (subset of silentOps host):
//   resolveScope, killTarget, sendToCemetery, meshesHas, logAction

import { msg } from '../../../logMessage';

/**
 * Codex "Split Power": a minion with two power numbers (attack / defence)
 * has an effective power equal to the AVERAGE of the two, rounded down,
 * when an effect needs to know its "power" (Black Plague / Great Famine's
 * weakest/strongest pick). A single-number unit has attack === defence, so
 * the average is that number. Aura deltas stack on both halves.
 */
export function splitPower(card) {
  const atk = Number(card?.currentAttack ?? card?.baseAttack ?? 0) + (card?._auraStatDelta || 0);
  const def = Number(card?.baseLife ?? card?.baseAttack ?? 0) + (card?._auraStatDelta || 0);
  return Math.floor((Math.max(0, atk) + Math.max(0, def)) / 2);
}

/**
 * @param {object} sourceCard
 * @param {object} entry - automation entry (carries scope)
 * @param {object} args
 *   direction       'min' | 'max'
 *   filter          optional card filter passed through to the scope
 *   dispel_if_none  optional. Dispel source if no eligible target
 * @param {object} host
 */
export function applyKillByPowerInScope(sourceCard, entry, args, host) {
  const direction = args?.direction === 'max' ? 'max' : 'min';
  const filter = { type: 'Minion', ...(args?.filter || {}) };
  const candidates = host.resolveScope(sourceCard, entry.scope, {
    filter,
    exclude_self: true,
  });
  if (candidates.length === 0) {
    if (args?.dispel_if_none && host.meshesHas(sourceCard.id)) {
      host.logAction('{source} finds no targets. Dispels', { source: sourceCard.name || msg('Aura') });
      host.sendToCemetery(sourceCard);
    }
    return;
  }
  // Pick the extremum deterministically. Stable iteration order from
  // resolveScope means the same call returns the same target across
  // identical board states. Good for replay / test parity.
  let best = candidates[0];
  let bestPower = splitPower(best);
  for (let i = 1; i < candidates.length; i++) {
    const p = splitPower(candidates[i]);
    const better = direction === 'min' ? p < bestPower : p > bestPower;
    if (better) { best = candidates[i]; bestPower = p; }
  }
  const slain = { source: sourceCard.name || msg('Aura'), target: best.name || msg('a minion'), n: bestPower };
  if (direction === 'min') host.logAction('{source} slays {target} (min power {n})', slain);
  else host.logAction('{source} slays {target} (max power {n})', slain);
  host.killTarget(best);
}
